import { useEffect, useState, useCallback, useLayoutEffect } from "react";
import { createPortal } from "react-dom";
import { X, ArrowRight, ArrowLeft, Sparkles } from "lucide-react";

const PAD = 8;

export function Tour({ steps, run, onClose }) {
  const [i, setI] = useState(0);
  const [rect, setRect] = useState(null);

  const step = steps[i];

  const measure = useCallback(() => {
    if (!step) return;
    if (!step.testid) { setRect(null); return; }
    const el = document.querySelector(`[data-testid="${step.testid}"]`);
    if (el) {
      el.scrollIntoView({ block: "center", behavior: "smooth" });
      const r = el.getBoundingClientRect();
      setRect({ top: r.top, left: r.left, width: r.width, height: r.height });
    } else {
      setRect(null);
    }
  }, [step]);

  useLayoutEffect(() => {
    if (!run) return;
    measure();
    const t = setTimeout(measure, 260);
    window.addEventListener("resize", measure);
    window.addEventListener("scroll", measure, true);
    return () => { clearTimeout(t); window.removeEventListener("resize", measure); window.removeEventListener("scroll", measure, true); };
  }, [run, measure]);

  useEffect(() => { if (run) setI(0); }, [run]);

  useEffect(() => {
    if (!run) return;
    window.dispatchEvent(new CustomEvent("notacora-tour-menu", { detail: !!step?.openMenu }));
  }, [run, i, step]);

  useEffect(() => () => {
    window.dispatchEvent(new CustomEvent("notacora-tour-menu", { detail: false }));
  }, []);

  if (!run || !step) return null;

  const finish = () => {
    window.dispatchEvent(new CustomEvent("notacora-tour-menu", { detail: false }));
    onClose?.();
  };
  const next = () => (i < steps.length - 1 ? setI(i + 1) : finish());
  const prev = () => setI(Math.max(0, i - 1));

  // Tooltip position
  const vw = window.innerWidth, vh = window.innerHeight;
  const cardW = Math.min(340, vw - 24);
  let cardStyle;
  if (rect) {
    const below = rect.top + rect.height + 12 + 180 < vh;
    const top = below ? rect.top + rect.height + 14 : Math.max(12, rect.top - 200);
    let left = rect.left + rect.width / 2 - cardW / 2;
    left = Math.max(12, Math.min(left, vw - cardW - 12));
    cardStyle = { top, left, width: cardW };
  } else {
    cardStyle = { top: vh / 2 - 110, left: vw / 2 - cardW / 2, width: cardW };
  }

  return createPortal(
    <div className="fixed inset-0 z-[95]" data-testid="tour-overlay">
      {rect ? (
        <div
          className="fixed rounded-xl pointer-events-none transition-all duration-300"
          style={{
            top: rect.top - PAD, left: rect.left - PAD,
            width: rect.width + PAD * 2, height: rect.height + PAD * 2,
            boxShadow: "0 0 0 9999px rgba(15,23,42,0.72)",
            border: "2px solid #6366f1",
          }}
        />
      ) : (
        <div className="fixed inset-0" style={{ background: "rgba(15,23,42,0.72)" }} />
      )}

      <div className="fixed bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl shadow-2xl p-5 transition-all duration-300" style={cardStyle} data-testid="tour-card">
        <div className="flex items-center justify-between mb-2">
          <span className="inline-flex items-center gap-1.5 font-heading font-medium text-sm text-indigo-500"><Sparkles size={15} /> {step.title}</span>
          <button onClick={finish} className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200" data-testid="tour-skip"><X size={16} /></button>
        </div>
        <p className="font-body text-sm text-slate-600 dark:text-slate-300 leading-relaxed">{step.text}</p>
        <div className="flex items-center justify-between mt-4 gap-2">
          <span className="font-mono2 text-[11px] text-slate-400 shrink-0">{i + 1} / {steps.length}</span>
          <div className="flex items-center gap-2 shrink-0">
            {i > 0 && <button onClick={prev} className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 p-1.5" data-testid="tour-prev"><ArrowLeft size={16} /></button>}
            <button onClick={next} className="inline-flex items-center gap-1.5 bg-indigo-600 hover:bg-indigo-500 text-white text-sm font-medium px-4 py-2 rounded-lg transition-colors" data-testid="tour-next">
              {i < steps.length - 1 ? <>Siguiente <ArrowRight size={15} /></> : "¡Listo!"}
            </button>
          </div>
        </div>
        <button onClick={finish} className="font-mono2 text-[11px] text-slate-400 hover:text-slate-500 mt-3" data-testid="tour-skip-text">Saltar tour</button>
      </div>
    </div>,
    document.body
  );
}
